import React from 'react';
import { useDispatch } from 'react-redux';
import { DeleteTask, PatchTask, PostTask } from '../../redux/taskSlice';
import { ChevronDown20Regular, ArrowSortUp16Regular } from '@fluentui/react-icons';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import styles from './css/index.module.css';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import ResizableColumnHeader from './ResizableColumnHeader';
import HierarchicalTaskRow from './HierarchicalTaskRow';
const TasksTable = ({
    filteredData,
    setFilteredData,
    projectUsersAll,
    modalVisible,
    setModalVisible,
    selectedRow,
    setSelectedRow,
    isAddingNewTask,
    setIsAddingNewTask,
    newTaskText,
    setNewTaskText,
    selectedProject,
    isColumnVisible,
    onOpenSidebar,
    selectedRows,
    setSelectedRows,
    groupByColumn,
    collapsedGroups,
    onToggleGroup,
    onDragEnd,
    sensors,
    expandedTasks,
    setExpandedTasks,
    addNewTaskFocusTrigger,
    sortColumn,
    sortOrder,
    onRequestSort,
}) => {
    const deleteModalRef = React.useRef(null);
    const newTaskInputRef = React.useRef(null);
    const addRowRef = React.useRef(null);
    const dispatch = useDispatch();
    const [editingTaskId, setEditingTaskId] = React.useState(null);
    const [editingTaskText, setEditingTaskText] = React.useState('');
    const tableContainerRef = React.useRef(null);
    const [highlightAddRow, setHighlightAddRow] = React.useState(false);
    const [groupedNewTaskText, setGroupedNewTaskText] = React.useState({});
    // Column width state management
    const [columnWidths, setColumnWidths] = React.useState({
        checkbox: 32,
        task: 408,
        owner: 56,
        status: 104,
        timeline: 180,
        duration: 140,
        deadline: 160,
        priority: 104,
        workstream: 320,
        projectPhase: 200,
        tag: 240,
        lastUpdated: 120,
        addedOn: 120,
        actions: 30,
    });

    // Column order state (excluding checkbox and actions)
    const defaultColumnOrder = ['task', 'owner', 'status', 'timeline', 'deadline', 'duration', 'priority', 'workstream', 'tag', 'projectPhase', 'lastUpdated', 'addedOn'];
    const [columnOrder, setColumnOrder] = React.useState(() => {
        try {
            const saved = localStorage.getItem('taskTableColumnOrder');
            if (saved) {
                const parsed = JSON.parse(saved);
                // Ensure saved order contains only known columns and include any new ones
                const cleaned = defaultColumnOrder.filter(c => parsed.includes(c));
                const missing = defaultColumnOrder.filter(c => !cleaned.includes(c));
                return [...cleaned, ...missing];
            }
        } catch (e) {}
        return defaultColumnOrder;
    });

    React.useEffect(() => {
        try {
            localStorage.setItem('taskTableColumnOrder', JSON.stringify(columnOrder));
        } catch (e) {}
    }, [columnOrder]);

    // Flatten all tasks (supports grouped + non-grouped data)
    const allTasks = React.useMemo(() => {
        if (filteredData?.isGrouped) {
            return filteredData.groups?.flatMap(group => group.tasks) || [];
        }
        return filteredData?.data || filteredData || [];
    }, [filteredData]);

    // Build a parent -> children map for fast descendant lookups
    const childrenByParentId = React.useMemo(() => {
        const map = new Map();
        allTasks.forEach(t => {
            if (!t?.parent_task) return;
            const parentId = t.parent_task;
            const existing = map.get(parentId);
            if (existing) existing.push(t);
            else map.set(parentId, [t]);
        });
        return map;
    }, [allTasks]);

    // Return all descendant task IDs for a given task ID (all levels)
    const getDescendantTaskIds = React.useCallback(
        taskId => {
            const result = [];
            const visited = new Set();
            const stack = [...(childrenByParentId.get(taskId) || [])];

            while (stack.length > 0) {
                const t = stack.pop();
                if (!t) continue;
                if (visited.has(t.id)) continue;
                visited.add(t.id);

                result.push(t.id);
                const children = childrenByParentId.get(t.id);
                if (children && children.length > 0) {
                    stack.push(...children);
                }
            }

            return result;
        },
        [childrenByParentId],
    );

    const handleColumnResize = (columnKey, newWidth) => {
        setColumnWidths(prev => ({
            ...prev,
            [columnKey]: newWidth,
        }));
    };

    // Sortable header cell
    const SortableHeaderCell = ({ id, width, children }) => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
        const style = {
            // Remove scale factors to avoid width/height distortion while dragging
            transform: transform ? `translate3d(${Math.round(transform.x)}px, ${Math.round(transform.y)}px, 0)` : undefined,
            transition,
            opacity: isDragging ? 0.6 : 1,
            width: `${width}px`,
            minWidth: `${width}px`,
            maxWidth: `${width}px`,
            '--column-width': `${width}px`,
            cursor: isDragging ? 's-resize' : undefined,
        };
        return (
            <th ref={setNodeRef} style={style} className={`${styles.headerRowItem} ${isDragging ? 'isDragging' : ''}`} data-sortable-id={id} {...attributes} {...listeners}>
                <div style={{ width: '100%' }}>{children}</div>
            </th>
        );
    };

    // Focus the bottom add-new-task input and scroll to bottom when activated
    const hasInitializedAddNewTask = React.useRef(false);

    React.useEffect(() => {
        // Avoid auto-scrolling on initial mount so the page doesn't jump down
        if (!hasInitializedAddNewTask.current) {
            hasInitializedAddNewTask.current = true;
            return;
        }

        if (isAddingNewTask && expandedTasks.size === 0 && !groupByColumn) {
            // Scroll the table container to bottom and focus the input
            requestAnimationFrame(() => {
                const container = document.getElementById('style-3-scroll');
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
                if (addRowRef.current) {
                    addRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
                if (newTaskInputRef.current) {
                    newTaskInputRef.current.focus();
                }
            });

            // Trigger 1s highlight animation on the add-new-task row
            setHighlightAddRow(true);
            const timeoutId = setTimeout(() => {
                setHighlightAddRow(false);
            }, 1000);

            return () => {
                clearTimeout(timeoutId);
            };
        }
    }, [isAddingNewTask, expandedTasks.size, addNewTaskFocusTrigger, groupByColumn]);

    // Hierarchy helper functions
    const getRootTasks = tasks => {
        return tasks.filter(task => !task.parent_task);
    };

    const handleToggleExpanded = taskId => {
        setExpandedTasks(prev => {
            const newSet = new Set(prev);
            if (newSet.has(taskId)) {
                // If task is already expanded, collapse it and all its children
                const allTasks = filteredData?.isGrouped ? filteredData.groups?.flatMap(group => group.tasks) || [] : filteredData?.data || filteredData || [];

                // Recursively find all children of this task
                const getAllChildren = parentId => {
                    const directChildren = allTasks.filter(t => t.parent_task === parentId);
                    let allChildren = [...directChildren];
                    directChildren.forEach(child => {
                        allChildren = [...allChildren, ...getAllChildren(child.id)];
                    });
                    return allChildren;
                };

                const childrenToCollapse = getAllChildren(taskId);
                childrenToCollapse.forEach(child => newSet.delete(child.id));
                newSet.delete(taskId);
            } else {
                // Add the current task to expanded set
                // Allow multiple rows to be expanded simultaneously
                newSet.add(taskId);
            }
            return newSet;
        });
    };

    const handleAddChildTask = async (parentTaskId, childTaskText) => {
        if (childTaskText.trim()) {
            try {
                await dispatch(
                    PostTask({
                        task: childTaskText.trim(),
                        project: selectedProject?.id,
                        parent_task: parentTaskId,
                        status: 'NOT_STARTED',
                    }),
                );
            } catch (error) {
                console.error('Error creating child task:', error);
            }
        }
    };
    const handleStatusChange = async (task, newStatus) => {
        try {
            // Prepare the update body
            const updateBody = { status: newStatus };

            // If task is being marked as completed and it's currently starred, unstar it
            if ((newStatus === 'COMPLETED' || newStatus === 'COMPLETE') && task.starred) {
                updateBody.starred = false;
            }

            await dispatch(
                PatchTask({
                    body: updateBody,
                    id: task.id,
                    suppressToast: true,
                }),
            );
        } catch (error) {
            console.error('Error updating task status:', error);
        }
    };

    const handlePriorityChange = async (task, newPriority) => {
        try {
            await dispatch(
                PatchTask({
                    body: { priority: newPriority },
                    id: task.id,
                    suppressToast: true,
                }),
            );
        } catch (error) {
            console.error('Error updating task priority:', error);
        }
    };

    const handleTagSelect = async (task, tag) => {
        try {
            // Toggle tag membership in the task's tags array.
            const tagName = tag?.name || '';
            const existingTags = Array.isArray(task.tags)
                ? task.tags
                : task?.tag_repr
                ? [task.tag_repr]
                : [];

            let nextTags;
            if (!tagName) {
                // Clear all tags when tag is null/undefined
                nextTags = [];
            } else if (existingTags.includes(tagName)) {
                // Remove tag if it is already present
                nextTags = existingTags.filter(t => t !== tagName);
            } else {
                // Add tag
                nextTags = [...existingTags, tagName];
            }

            await dispatch(
                PatchTask({
                    body: { tags: nextTags },
                    id: task.id,
                    suppressToast: true,
                }),
            );
        } catch (error) {
            console.error('Error updating task tag:', error);
        }
    };

    const handleWorkstreamSelect = async (task, workstream) => {
        try {
            const newWorkstreamName = workstream?.name || '';
            await dispatch(
                PatchTask({
                    body: { workstream_repr: newWorkstreamName },
                    id: task.id,
                    suppressToast: true,
                }),
            );
        } catch (error) {
            console.error('Error updating task workstream:', error);
        }
    };

    const handleUserAssignment = async (task, user) => {
        // This function is called after the API call is successful
        // The actual API call is handled in the UserAssignmentMenu component
        console.log('User assigned:', user);
    };

    const handleDeadlineSelect = async (task, deadlineData) => {
        // This function is called after the API call is successful
        // The actual API call is handled in the DeadlinePickerMenu component
        console.log('Deadline updated:', deadlineData);
    };

    const handleProjectPhaseChange = async (task, newPhase) => {
        try {
            await dispatch(
                PatchTask({
                    body: { project_phase: newPhase || null },
                    id: task.id,
                    suppressToast: true,
                }),
            );
        } catch (error) {
            console.error('Error updating project phase:', error);
        }
    };

    const parseGroupDeadlineToIso = groupName => {
        if (!groupName || groupName === 'No Deadline') return '';
        const parts = groupName.split('/');
        if (parts.length !== 3) return '';

        const [dd, mm, yy] = parts.map(part => part.trim());
        const day = Number(dd);
        const month = Number(mm);
        const shortYear = Number(yy);

        if (!day || !month || Number.isNaN(shortYear)) return '';

        const fullYear = shortYear < 100 ? 2000 + shortYear : shortYear;
        const parsedDate = new Date(fullYear, month - 1, day);
        if (Number.isNaN(parsedDate.getTime())) return '';

        const normalizedDay = String(day).padStart(2, '0');
        const normalizedMonth = String(month).padStart(2, '0');
        return `${fullYear}-${normalizedMonth}-${normalizedDay}`;
    };

    const getGroupInferredFields = groupName => {
        if (!filteredData?.isGrouped || !groupByColumn || !groupName) return {};

        switch (groupByColumn) {
            case 'status': {
                const statusMap = {
                    'Not Started': 'NOT_STARTED',
                    'Working on it': 'WORKING_ON_IT',
                    Completed: 'COMPLETED',
                    Stuck: 'STUCK',
                    Cancelled: 'CANCELLED',
                    'On Track': 'ON_TRACK',
                    'Off Track': 'OFF_TRACK',
                };
                const status = statusMap[groupName];
                return status ? { status } : {};
            }
            case 'priority': {
                const priorityMap = {
                    High: 'HIGH',
                    Medium: 'MEDIUM',
                    Low: 'LOW',
                };
                const priority = priorityMap[groupName];
                return priority ? { priority } : {};
            }
            case 'owner': {
                if (groupName === 'Unassigned') return {};
                const matchedUser = projectUsersAll?.find(user => user?.value?.full_name === groupName);
                const assignedTo = matchedUser?.value?.user_id;
                return assignedTo ? { assigned_to: assignedTo } : {};
            }
            case 'workstream':
                return groupName === 'No Workstream' ? {} : { workstream_repr: groupName };
            case 'project_phase':
                return groupName === 'No Project Phase' ? {} : { project_phase: groupName };
            case 'deadline': {
                const deadline = parseGroupDeadlineToIso(groupName);
                return deadline ? { deadline } : {};
            }
            default:
                return {};
        }
    };

    const scrollTableToBottom = () => {
        setTimeout(() => {
            if (tableContainerRef.current) {
                tableContainerRef.current.scrollTo({
                    top: tableContainerRef.current.scrollHeight,
                    behavior: 'smooth',
                });
            } else {
                const container = document.getElementById('style-3-scroll');
                if (container) {
                    container.scrollTo({
                        top: container.scrollHeight,
                        behavior: 'smooth',
                    });
                }
            }
        }, 100);
    };

    const createTaskFromInput = async groupName => {
        const activeTaskText = filteredData?.isGrouped ? groupedNewTaskText[groupName] || '' : newTaskText;
        const taskText = activeTaskText.trim();
        if (!taskText) return;

        const payload = {
            task: taskText,
            project: selectedProject?.id,
            status: 'NOT_STARTED',
            ...getGroupInferredFields(groupName),
        };

        await dispatch(PostTask(payload));
        if (filteredData?.isGrouped) {
            setGroupedNewTaskText(prev => ({ ...prev, [groupName]: '' }));
        } else {
            setNewTaskText('');
        }
        scrollTableToBottom();
    };

    const handleTaskTextClick = (e, task) => {
        e.stopPropagation();
        setEditingTaskId(task.id);
        // Convert HTML to plain text for editing
        const plainText = task.task ? task.task.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ') : '';
        setEditingTaskText(plainText);
    };

    const handleTaskTextSave = async task => {
        // Only save if text has actually changed
        const originalText = task.task ? task.task.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ') : '';
        if (editingTaskText.trim() !== originalText.trim()) {
            try {
                await dispatch(
                    PatchTask({
                        body: { task: editingTaskText },
                        id: task.id,
                        suppressToast: true,
                    }),
                );
            } catch (error) {
                console.error('Error updating task text:', error);
            }
        }
        setEditingTaskId(null);
        setEditingTaskText('');
    };

    const handleTaskTextCancel = () => {
        setEditingTaskId(null);
        setEditingTaskText('');
    };

    const handleTaskTextKeyPress = (e, task) => {
        e.stopPropagation(); // Prevent event bubbling to drag handlers
        if (e.key === 'Enter') {
            handleTaskTextSave(task);
        } else if (e.key === 'Escape') {
            handleTaskTextCancel();
        }
    };

    const getUserInfo = createdBy => {
        const user = projectUsersAll?.find(user => user?.value?.user_id === createdBy);
        return user?.value || null;
    };

    const handleStarClick = async (e, task) => {
        e.stopPropagation();

        if (!task.starred) {
            // Get all tasks from filteredData (handle both grouped and non-grouped)
            const allTasks = filteredData?.isGrouped ? filteredData.groups?.flatMap(group => group.tasks) || [] : filteredData?.data || filteredData || [];

            // Unstar any other starred task first
            const otherStarred = allTasks.find(t => t.id !== task.id && t.starred);
            if (otherStarred) {
                await dispatch(PatchTask({ body: { starred: false }, id: otherStarred.id, suppressToast: true }));
            }
            // Star this task
            await dispatch(PatchTask({ body: { starred: true }, id: task.id }));
        } else {
            // Unstar this task
            await dispatch(PatchTask({ body: { starred: false }, id: task.id }));
        }
    };

    const handleEdit = task => {
        setSelectedRow(task);
        setModalVisible(true);
    };

    const handleDelete = task => {
        setSelectedRow(task);
        deleteModalRef?.current?.click();
    };

    const handleSelectRow = taskId => {
        const descendantIds = getDescendantTaskIds(taskId);
        setSelectedRows(prev => {
            const next = new Set(prev);
            const isSelected = next.has(taskId);

            if (isSelected) {
                next.delete(taskId);
                descendantIds.forEach(id => next.delete(id));
            } else {
                next.add(taskId);
                descendantIds.forEach(id => next.add(id));
            }

            return Array.from(next);
        });
    };

    const handleSelectAll = (groupTasks = null) => {
        // If groupTasks is provided, we're in grouped mode and should only select tasks from this group
        const tasksToSelect = groupTasks || (filteredData?.isGrouped ? filteredData.groups?.flatMap(group => group.tasks) || [] : filteredData?.data || filteredData || []);

        if (groupTasks) {
            // Grouped mode: only select/deselect tasks from this specific group
            const groupTaskIds = groupTasks.map(task => task.id);
            const allGroupTasksSelected = groupTaskIds.every(id => selectedRows.includes(id));

            if (allGroupTasksSelected) {
                // Deselect all tasks from this group
                setSelectedRows(prev => prev.filter(id => !groupTaskIds.includes(id)));
            } else {
                // Select all tasks from this group (keep other groups' selections)
                setSelectedRows(prev => {
                    const otherGroupSelections = prev.filter(id => !groupTaskIds.includes(id));
                    return [...otherGroupSelections, ...groupTaskIds];
                });
            }
        } else {
            // Non-grouped mode: select/deselect all tasks
            if (selectedRows.length === tasksToSelect?.length) {
                setSelectedRows([]);
            } else {
                setSelectedRows(tasksToSelect?.map(task => task.id) || []);
            }
        }
    };

    const getStatusInfo = status => {
        const statusMap = {
            // Backend status values (what actually comes from API)
            NOT_STARTED: { bg: '#C0C6D8', text: 'Not Started' },
            WORKING_ON_IT: { bg: 'var(--RAG-Amber)', text: 'Working on it' },
            COMPLETED: { bg: 'var(--RAG-Green)', text: 'Completed' },
            STUCK: { bg: 'var(--RAG-Red)', text: 'Stuck' },
            CANCELLED: { bg: '#989BAD', text: 'Cancelled' },
            ON_TRACK: { bg: '#4F8A6F', text: 'On Track' },
            OFF_TRACK: { bg: '#9E4F4F', text: 'Off Track' },
            NOT_APPLICABLE: { bg: '#C0C6D8', text: 'Not Started' },
            // Legacy status support (for backward compatibility)
            PENDING: { bg: 'var(--RAG-Amber)', text: 'Working on it' },
            COMPLETE: { bg: 'var(--RAG-Green)', text: 'Completed' },
            // Fallback for unknown statuses
            'Not started': { bg: '#C0C6D8', text: 'Not Started' },
            'Working on it': { bg: 'var(--RAG-Amber)', text: 'Working on it' },
            Completed: { bg: 'var(--RAG-Green)', text: 'Completed' },
            Stuck: { bg: 'var(--RAG-Red)', text: 'Stuck' },
            Cancelled: { bg: '#989BAD', text: 'Cancelled' },
            'On Track': { bg: '#4F8A6F', text: 'On Track' },
            'Off Track': { bg: '#9E4F4F', text: 'Off Track' },
            'Not Applicable': { bg: '#C0C6D8', text: 'Not Started' },
        };

        // If status exists in map, use it; otherwise return empty status
        if (statusMap[status]) {
            return statusMap[status];
        }

        // Default missing statuses to Not Started.
        return { bg: '#C0C6D8', text: 'Not Started' };
    };

    const formatLastUpdated = lastUpdated => {
        if (!lastUpdated) return '-- --';

        try {
            const date = new Date(lastUpdated);
            const now = new Date();
            const diffInMs = now - date;
            const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
            const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));

            if (diffInMinutes < 1) {
                return 'just now';
            } else if (diffInMinutes < 60) {
                return `${diffInMinutes} min ago`;
            } else if (diffInHours < 24) {
                return `${diffInHours} hrs ago`;
            } else {
                // Format as DD/MM/YYYY
                return date.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                });
            }
        } catch (error) {
            console.error('Error formatting last updated date:', error);
            return '';
        }
    };

    const formatDeadline = (deadline) => {
        if (!deadline) return '-- --';

        try {
            const deadlineDate = new Date(deadline);
            return deadlineDate.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric', // DD/MM/YYYY
            });
        } catch (error) {
            console.error('Error formatting deadline:', error);
            return '-- --';
        }
    };

    const getPriorityInfo = priority => {
        const priorityMap = {
            HIGH: { bg: '#2B35AF', text: 'High' },
            MEDIUM: { bg: '#4361EE', text: 'Medium' },
            LOW: { bg: '#4895EF', text: 'Low' },
            '': { bg: '#C0C6D8', text: '' },
        };

        // If priority exists in map, use it; otherwise return empty priority
        if (priorityMap[priority]) {
            return priorityMap[priority];
        }

        // Return empty priority for undefined/null values
        return priorityMap[''];
    };

    // Helper function to render a single table
    const renderTable = (tasks, groupName = null) => {
        const allTasks = filteredData?.isGrouped ? filteredData.groups?.flatMap(group => group.tasks) || [] : filteredData?.data || filteredData || [];
        const renderSortLabel = (colKey, label) => {
            const isActive = sortColumn === colKey;
            const isDesc = sortOrder === 'desc';
            return (
                <div
                    role="button"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' }}
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        onRequestSort && onRequestSort(colKey);
                    }}>
                    <span>{label}</span>
                    <ArrowSortUp16Regular
                        color={isActive ? 'var(--Brand-Secondary)' : 'rgba(130, 154, 177, 0.8)'}
                        style={{
                            transform: isActive && isDesc ? 'rotate(180deg)' : 'none',
                            transition: 'transform 120ms ease',
                        }}
                    />
                </div>
            );
        };

        return (
            <div className={styles.tableContainer} ref={tableContainerRef} id="style-3-scroll" style={filteredData?.isGrouped ? { height: 'auto' } : undefined}>
                {groupName && (
                    <div className="mt-3 mb-2">
                        <span className={styles.todoText} onClick={() => onToggleGroup(groupName)} style={{ cursor: 'pointer' }}>
                            {!collapsedGroups.has(groupName) ? (
                                <ChevronDown20Regular className="me-1" />
                            ) : (
                                <ChevronDown20Regular className="me-1" style={{ transform: 'rotate(-90deg)' }} />
                            )}
                            {groupName} ({tasks.length} {tasks.length === 1 ? 'task' : 'tasks'})
                        </span>
                    </div>
                )}
                {!(groupName && collapsedGroups.has(groupName)) && (
                    <table className="w-100">
                        <thead>
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragStart={() => {
                                    // Show down arrow cursor while dragging columns
                                    document.body.style.cursor = 's-resize';
                                }}
                                onDragEnd={event => {
                                    const { active, over } = event;
                                    if (!over || active.id === over.id) return;
                                    const oldIndex = columnOrder.indexOf(String(active.id));
                                    const newIndex = columnOrder.indexOf(String(over.id));
                                    if (oldIndex === -1 || newIndex === -1) return;
                                    const updated = [...columnOrder];
                                    const [moved] = updated.splice(oldIndex, 1);
                                    updated.splice(newIndex, 0, moved);
                                    setColumnOrder(updated);
                                    // Restore cursor after drag
                                    document.body.style.cursor = '';
                                }}
                                onDragCancel={() => {
                                    // Restore cursor if drag is cancelled
                                    document.body.style.cursor = '';
                                }}>
                                <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
                                    <tr
                                        className={styles.tableHeader}
                                        onMouseEnter={e => {
                                            const checkbox = e.currentTarget.querySelector('input[type="checkbox"]');
                                            if (checkbox) {
                                                checkbox.style.opacity = '1';
                                            }
                                        }}
                                        onMouseLeave={e => {
                                            const checkbox = e.currentTarget.querySelector('input[type="checkbox"]');
                                            if (checkbox) {
                                                checkbox.style.opacity = '0.5';
                                            }
                                        }}>
                                        <th className={styles.headerRowItem} style={{ width: columnWidths.checkbox }}>
                                            <input
                                                className={styles.checkboxTable}
                                                type="checkbox"
                                                checked={
                                                    groupName
                                                        ? tasks.every(task => selectedRows.includes(task.id)) && tasks.length > 0
                                                        : selectedRows.length === allTasks?.length && allTasks?.length > 0
                                                }
                                                onChange={() => handleSelectAll(groupName ? tasks : null)}
                                                style={{ opacity: 0.5 }}
                                            />
                                        </th>

                                        {columnOrder.map(col => {
                                            if (!isColumnVisible(col)) return null;
                                            if (col === 'task') {
                                                return (
                                                    <SortableHeaderCell key={col} id={col} width={columnWidths.task}>
                                                        <ResizableColumnHeader
                                                            width={columnWidths.task}
                                                            minWidth={408}
                                                            maxWidth={800}
                                                            onResize={newWidth => handleColumnResize('task', newWidth)}>
                                                            Task
                                                        </ResizableColumnHeader>
                                                    </SortableHeaderCell>
                                                );
                                            }
                                            if (col === 'workstream') {
                                                return (
                                                    <SortableHeaderCell key={col} id={col} width={columnWidths.workstream}>
                                                        <ResizableColumnHeader
                                                            width={columnWidths.workstream}
                                                            minWidth={120}
                                                            maxWidth={500}
                                                            onResize={newWidth => handleColumnResize('workstream', newWidth)}>
                                                            Workstream
                                                        </ResizableColumnHeader>
                                                    </SortableHeaderCell>
                                                );
                                            }
                                            if (col === 'tag') {
                                                return (
                                                    <SortableHeaderCell key={col} id={col} width={columnWidths.tag}>
                                                        <ResizableColumnHeader
                                                            width={columnWidths.tag}
                                                            minWidth={100}
                                                            maxWidth={400}
                                                            onResize={newWidth => handleColumnResize('tag', newWidth)}>
                                                            Tag
                                                        </ResizableColumnHeader>
                                                    </SortableHeaderCell>
                                                );
                                            }
                                            if (col === 'projectPhase') {
                                                return (
                                                    <SortableHeaderCell key={col} id={col} width={columnWidths.projectPhase}>
                                                        <ResizableColumnHeader
                                                            width={columnWidths.projectPhase}
                                                            minWidth={140}
                                                            maxWidth={360}
                                                            onResize={newWidth => handleColumnResize('projectPhase', newWidth)}>
                                                            Project Phase
                                                        </ResizableColumnHeader>
                                                    </SortableHeaderCell>
                                                );
                                            }
                                            const labelMap = {
                                                owner: 'Owner',
                                                status: 'Status',
                                                timeline: 'Timeline',
                                                duration: 'Duration',
                                                deadline: 'Deadline',
                                                priority: 'Priority',
                                                projectPhase: 'Project Phase',
                                                lastUpdated: 'Last Updated',
                                                addedOn: 'Added On',
                                            };
                                            const headerContent =
                                                col === 'lastUpdated'
                                                    ? renderSortLabel('lastUpdated', labelMap.lastUpdated)
                                                    : col === 'addedOn'
                                                    ? renderSortLabel('addedOn', labelMap.addedOn)
                                                    : labelMap[col];
                                            return (
                                                <SortableHeaderCell key={col} id={col} width={columnWidths[col]}>
                                                    {headerContent}
                                                </SortableHeaderCell>
                                            );
                                        })}

                                        <th className={styles.headerRowItem} style={{ width: columnWidths.actions }}></th>
                                    </tr>
                                </SortableContext>
                            </DndContext>
                        </thead>

                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragStart={event => {
                                console.log('Drag started:', event);
                                // Add modifier key detection
                                event.active.data.current = {
                                    ...event.active.data.current,
                                    modifierKey: event.active.data.current?.modifierKey || false,
                                };
                            }}
                            onDragOver={event => {
                                // Add visual feedback during drag
                                const { over } = event;
                                if (over) {
                                    const overElement = document.querySelector(`[data-rbd-droppable-id="${over.id}"]`);
                                    if (overElement) {
                                        overElement.classList.add(styles.dragOver);
                                    }
                                }
                            }}
                            onDragEnd={event => onDragEnd(event, groupName)}>
                            <SortableContext items={tasks?.map(task => task.id.toString()) || []} strategy={verticalListSortingStrategy}>
                                <tbody>
                                    {getRootTasks(tasks)?.map((task, index) => (
                                        <HierarchicalTaskRow
                                            key={task.id}
                                            task={task}
                                            index={index}
                                            editingTaskId={editingTaskId}
                                            setEditingTaskId={setEditingTaskId}
                                            editingTaskText={editingTaskText}
                                            setEditingTaskText={setEditingTaskText}
                                            handleTaskTextClick={handleTaskTextClick}
                                            handleTaskTextSave={handleTaskTextSave}
                                            handleTaskTextCancel={handleTaskTextCancel}
                                            handleTaskTextKeyPress={handleTaskTextKeyPress}
                                            handleStarClick={handleStarClick}
                                            handleUserAssignment={handleUserAssignment}
                                            handleDeadlineSelect={handleDeadlineSelect}
                                            handleStatusChange={handleStatusChange}
                                            handlePriorityChange={handlePriorityChange}
                                            handleTagSelect={handleTagSelect}
                                            handleWorkstreamSelect={handleWorkstreamSelect}
                                            handleEdit={handleEdit}
                                            handleDelete={handleDelete}
                                            handleSelectRow={handleSelectRow}
                                            selectedRows={selectedRows}
                                            isColumnVisible={isColumnVisible}
                                            columnWidths={columnWidths}
                                            columnOrder={columnOrder}
                                            projectUsersAll={projectUsersAll}
                                            filteredData={filteredData}
                                            onOpenSidebar={onOpenSidebar}
                                            getUserInfo={getUserInfo}
                                            getStatusInfo={getStatusInfo}
                                            getPriorityInfo={getPriorityInfo}
                                            formatLastUpdated={formatLastUpdated}
                                            formatDeadline={formatDeadline}
                                            level={0}
                                            isExpanded={expandedTasks.has(task.id)}
                                            onToggleExpanded={handleToggleExpanded}
                                            onAddChildTask={handleAddChildTask}
                                            expandedTasks={expandedTasks}
                                            handleProjectPhaseChange={handleProjectPhaseChange}
                                        />
                                    ))}
                                    {isAddingNewTask &&
                                        expandedTasks.size === 0 &&
                                        (() => {
                                            // Calculate total number of columns: checkbox + visible columns + actions
                                            const visibleColumnsCount = columnOrder.filter(col => isColumnVisible(col)).length;
                                            const totalColumns = 1 + visibleColumnsCount + 1; // checkbox + visible columns + actions
                                            const activeNewTaskText = filteredData?.isGrouped ? groupedNewTaskText[groupName] || '' : newTaskText;

                                            return (
                                                <tr
                                                    ref={addRowRef}
                                                    className={`${styles.tableRow} ${highlightAddRow && !groupByColumn ? styles.highlightAddRow : ''}`}
                                                    style={{ backgroundColor: '#F3FDFE', position: 'sticky', bottom: 0, zIndex: 1 }}>
                                                    {/* Single cell with colspan spanning all columns */}
                                                    <td className={styles.tableRowItem} colSpan={totalColumns}>
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <div className="flex-fill">
                                                                <input
                                                                    ref={newTaskInputRef}
                                                                    type="text"
                                                                    className={'form-control form-control-sm ' + styles.taskTextField}
                                                                    value={activeNewTaskText}
                                                                    style={{ border: '1px solid #dddee4', marginLeft: '36px', maxWidth: '400px' }}
                                                                    onChange={e => {
                                                                        if (filteredData?.isGrouped) {
                                                                            setGroupedNewTaskText(prev => ({
                                                                                ...prev,
                                                                                [groupName]: e.target.value,
                                                                            }));
                                                                        } else {
                                                                            setNewTaskText(e.target.value);
                                                                        }
                                                                    }}
                                                                    onKeyDown={async e => {
                                                                        if (e.key === 'Enter') {
                                                                            e.preventDefault();
                                                                            await createTaskFromInput(groupName);
                                                                        } else if (e.key === 'Escape') {
                                                                            if (filteredData?.isGrouped) {
                                                                                setGroupedNewTaskText(prev => ({ ...prev, [groupName]: '' }));
                                                                            } else {
                                                                                setNewTaskText('');
                                                                            }
                                                                        }
                                                                    }}
                                                                    onBlur={async () => {
                                                                        if (!activeNewTaskText.trim()) {
                                                                            if (filteredData?.isGrouped) {
                                                                                setGroupedNewTaskText(prev => ({ ...prev, [groupName]: '' }));
                                                                            } else {
                                                                                setNewTaskText('');
                                                                            }
                                                                            return;
                                                                        }
                                                                        await createTaskFromInput(groupName);
                                                                    }}
                                                                    placeholder="Add new task"
                                                                />
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })()}
                                </tbody>
                            </SortableContext>
                        </DndContext>
                    </table>
                )}
            </div>
        );
    };

    return (
        <>
            {filteredData?.isGrouped
                ? // Render multiple tables for grouped data
                  filteredData.groups?.map((group, groupIndex) => (
                      <div key={groupIndex} className={groupIndex > 0 ? 'mt-4' : ''}>
                          {renderTable(group.tasks, group.groupName)}
                      </div>
                  ))
                : // Render single table for non-grouped data
                  renderTable(filteredData?.data || filteredData || [])}

            {/* Delete Confirmation Modal */}
            <DoubleConfirmationModal
                message="Deleting a Task will permanently remove it and all associated comments from the Project."
                handleAction={() => {
                    if (selectedRow) {
                        dispatch(DeleteTask({ id: selectedRow.id }));
                        setSelectedRow(null);
                    }
                }}>
                <span ref={deleteModalRef} style={{ display: 'none' }}></span>
            </DoubleConfirmationModal>
        </>
    );
};

export default TasksTable;
